import { fromJS, Map } from 'immutable';
import * as Sentry from '@sentry/react';

import { getBillingPrice, getTier, isSubscribed, shapeQuoteForApp } from '../utils/billingV3';
import { isEbayInvalidMarketplace } from '../utils/shops';
import { getDiscountedPrice } from '../utils/billing';
import { createController } from '../utils/controller';
import { navigateTo } from '../utils/navigation';
import { getSize } from '../utils/iterable/getSize';
import { isAfter } from '../utils/time';
import { isTrue } from '../utils/bool';
import api from '../utils/api';

import { BILLING_V3_TIMESTAMP, LEGACY_TIER_MAP, MULTI_SHOP_DISCOUNT, PLAN } from '../constants/billingV3';
import { BILLING_V3_PLUS_PREFERRED } from '../constants/user';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { TIER as LEGACY_TIER } from '../constants/billing';
import { EMPTY, SEPARATOR } from '../constants';
import { BETA_CHANNELS } from '../constants/features';
import { EBAY, ETSY } from '../constants/channels';
import { STATUSES } from '../constants/shops';
import { ENDPOINT } from '../constants/api';
import { MODALS } from '../constants/modal';

import actions from '../actions';

const customerController = createController();
const invoicesController = createController();
const shopsController = createController();
const manageController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    customerController,
    invoicesController,
    shopsController,
    manageController,
  ].forEach(abort);
}

function* cleanUp(reduction) {
  abortAll();
  return reduction.set('billingV3', EMPTY.MAP);
}

function* getCustomer(reduction) {
  abort(customerController);

  yield function effect(dispatch) {
    const { signal } = customerController.start();
    api.billingV3
      .getCustomer({ signal })
      .then(
        (response) => dispatch(actions.billingV3.getCustomer.fulfilled(response)),
        (error) => dispatch(actions.billingV3.getCustomer.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getCustomerFulfilled(reduction, response) {
  abort(customerController);

  let billing = reduction.get('billingV3') || EMPTY.MAP;

  if (billing.has('paymentError')) {
    billing = billing.delete('paymentError');
  }

  billing = billing.set('customer', fromJS(response));

  return reduction.set('billingV3', billing);
}

function* getCustomerRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(customerController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getInvoices(reduction) {
  function reduceShopIds(result, shopId) {
    const subscriptionId = reduction.getIn(['user', 'subscriptions', shopId, 'subscriptionId']);

    if (!subscriptionId) return result;

    const shop = reduction.getIn(['shops', 'byId', shopId]);

    return result.push(
      Map({
        channel: shop.get('channel'),
        domain: shop.get('domain'),
        db: shop.get('db'),
        shopId,
        shopName: shop.get('name'),
        subscriptionId,
      })
    );
  }

  const tabs = reduction.getIn(['shops', 'options']).reduce(reduceShopIds, EMPTY.LIST);

  if (getSize(tabs)) {
    yield function effect(dispatch) {
      dispatch(actions.billingV3.setInvoicesTab(0));
    };
  }

  return reduction.setIn(['billingV3', 'invoices'], Map({ tabs }));
}

function* getShops(reduction) {
  abort(shopsController);

  const empty = Map({ byId: EMPTY.MAP, ids: EMPTY.LIST });
  const subscriptions = reduction.getIn(['user', 'subscriptions']);

  if (!getSize(subscriptions)) return reduction.setIn(['billingV3', 'shops'], empty);

  const userShops = reduction.get('shops');
  const { signal } = shopsController.start();

  function addCount(count = 0) {
    return count + 1;
  }

  function reduceShopIds(result, shopId) {
    const subscription = reduction.getIn(['user', 'subscriptions', shopId]);
    const shop = userShops.getIn(['byId', shopId]);
    const isVelaOnlyShop = shop.get('isVelaOnlyShop');

    if (!subscription || isVelaOnlyShop) return result;

    const channel = shop.get('channel');
    const plan = subscription.get('plan');
    const db = shop.get('db');
    const shopName = shop.get('name');

    if (plan === PLAN.FREE || BETA_CHANNELS.has(channel)) {
      result.actions.push(
        actions.billingV3.getShop.fulfilled({
          shop: {
            channel,
            db,
            plan: PLAN.FREE,
            shopBadge: BETA_CHANNELS.has(channel) ? 'Beta' : 'Free',
            shopId,
            shopName,
          },
        })
      );

      result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
      return result;
    }

    if (subscription.get('frozen')) {
      const futurePlan = PLAN.PLUS;
      const frozen = true;

      result.actions.push(
        actions.billingV3.getShop({
          channel,
          db,
          plan,
          frozen,
          futurePlan,
          shopId,
          shopName,
          signal,
        })
      );

      result.shops = result.shops.update('connected', addCount).updateIn(['subscriptions', plan], addCount);
      result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));

      return result;
    }

    switch (channel) {
      case EBAY: {
        if (isEbayInvalidMarketplace(shop.get('syncStatus'))) {
          return result;
        }

        break;
      }

      default: {
        break;
      }
    }

    const planName = plan?.split(SEPARATOR.DOT)[0];

    switch (planName) {
      case PLAN.TRIAL: {
        const discount = subscription.get('discountPercent') || 0;
        const trialRemaining = subscription.get('trialRemaining') || 0;
        const trialExpired = trialRemaining < 1;
        const futurePlan = PLAN.PLUS;

        result.actions.push(
          actions.billingV3.getShop({
            channel,
            db,
            discount,
            dueDate: null,
            futurePlan,
            plan,
            shopId,
            shopName,
            signal,
            trialExpired,
          })
        );

        result.shops = result.shops.update('connected', addCount);
        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      case PLAN.PLUS:
      case PLAN.LITE: {
        const canceled = !!subscription.get('cancelAtPeriodEnd');
        const tierChange = !!subscription.get('tierChangeDetected');
        const subscriptionId = subscription.get('subscriptionId');
        const discount = subscription.get('discountPercent') || 0;
        const dueDate = subscription.get('dueDate');
        const tier = plan.split(SEPARATOR.DOT)[1];
        const price = getBillingPrice({ discount, plan: planName, tier });
        const undiscountedPrice = discount > 0
          ? getBillingPrice({ plan: planName, tier })
          : 0;

        const futurePlan = PLAN.PLUS;

        result.actions.push(
          actions.billingV3.getShop({
            canceled,
            channel,
            db,
            discount,
            dueDate,
            futurePlan,
            plan: planName,
            price,
            shopId,
            shopName,
            signal,
            subscriptionId,
            tier,
            tierChange,
            undiscountedPrice,
          })
        );

        if (!canceled) {
          result.shops = result.shops
            .update('connected', addCount)
            .updateIn(['subscriptions', planName], addCount);
        }

        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      case LEGACY_TIER.T_0:
      case LEGACY_TIER.T_251:
      case LEGACY_TIER.T_501:
      case LEGACY_TIER.T_1001:
      case LEGACY_TIER.T_2501:
      case LEGACY_TIER.T_5001:
      case LEGACY_TIER.T_7501:
      case LEGACY_TIER.T_10001:
      case LEGACY_TIER.T_20001:
      case LEGACY_TIER.T_30001:
      case LEGACY_TIER.T_40001:
      case LEGACY_TIER.T_50001:
      case LEGACY_TIER.T_60001:
      case LEGACY_TIER.T_70001: {
        const canceled = !!subscription.get('cancelAtPeriodEnd');
        const tierChange = !!subscription.get('tierChangeDetected');
        const subscriptionId = subscription.get('subscriptionId');
        const discount = subscription.get('discountPercent') || 0;
        const dueDate = subscription.get('dueDate');
        const tier = LEGACY_TIER_MAP[planName];
        const futurePlan = PLAN.PLUS;

        let price = subscription.get('price') || 0;
        let undiscountedPrice = 0;

        if (discount > 0) {
          undiscountedPrice = price;
          price = getDiscountedPrice({ discount, price });
        }

        result.actions.push(
          actions.billingV3.getShop({
            canceled,
            channel,
            db,
            discount,
            dueDate,
            futurePlan,
            plan: PLAN.VIP,
            price,
            shopId,
            shopName,
            signal,
            subscriptionId,
            tier,
            tierChange,
            undiscountedPrice,
          })
        );

        if (!canceled) {
          result.shops = result.shops.update('connected', addCount);
        }

        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      default: {
        Sentry.captureException('Unexpected shop plan', {
          extra: {
            db,
            shopId,
            subscription: subscription.toJS(),
          },
        });

        break;
      }
    }

    return result;
  }

  const { actions: actionsToDispatch, shops } = userShops
    .get('options')
    .reduce(reduceShopIds, { actions: [], shops: empty });

  if (getSize(actionsToDispatch)) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  return reduction.setIn(['billingV3', 'shops'], shops);
}

function* getShop(reduction, { signal, ...shop }) {
  yield function effect(dispatch) {
    api.billingV3
      .getSubscription({ db: shop.db, shopId: shop.shopId, signal })
      .then(
        (response) => {
          const { lite: { product_count: listings }} = response;
          dispatch(
            actions.billingV3.getShop.fulfilled({
              ...shop,
              listings: listings || 0,
              quote: shapeQuoteForApp(response),
            })
          );
        },
        (error) => dispatch(actions.billingV3.getShop.rejected({ error, shop, signal }))
      );
  };

  return reduction;
}

function* getShopFulfilled(reduction, shop) {
  let state = reduction.getIn(['billingV3', 'shops']);
  const { canceled, dueDate, listings, plan, shopId } = shop;

  switch (plan) {
    case PLAN.LITE:
    case PLAN.PLUS: {
      const tier = getTier({ listings });

      shop.discount = state.get('connected') > 1 ? MULTI_SHOP_DISCOUNT : 0;

      if (tier !== shop.tier) {
        shop.tier = tier;
        shop.price = getBillingPrice({ discount: shop.discount, plan, tier: shop.tier });
        shop.undiscountedPrice = shop.discount > 0
          ? getBillingPrice({ plan, tier: shop.tier })
          : 0;
      }

      if (!canceled) {
        state = state.set('total', state = state.get('total', 0) + shop.price);
      }

      break;
    }

    case PLAN.TRIAL: {
      shop.tier = getTier({ listings });
      shop.discount = state.get('connected') > 1 ? MULTI_SHOP_DISCOUNT : 0;
      shop.price = getBillingPrice({ discount: shop.discount, plan: shop.futurePlan, tier: shop.tier });
      shop.undiscountedPrice = shop.discount > 0
        ? getBillingPrice({ plan: shop.futurePlan, tier: shop.tier })
        : 0;

      break;
    }

    case PLAN.VIP: {
      if (isAfter(new Date(dueDate), BILLING_V3_TIMESTAMP)) {
        const futurePlan = isTrue(reduction.getIn(['user', `${BILLING_V3_PLUS_PREFERRED}${shopId}`]))
          ? PLAN.PLUS
          : PLAN.LITE;

        shop.tier = getTier({ listings });
        shop.discount = state.get('connected') > 1 ? MULTI_SHOP_DISCOUNT : 0;
        shop.price = getBillingPrice({ discount: shop.discount, plan: futurePlan, tier: shop.tier });
        shop.undiscountedPrice = shop.discount > 0
          ? getBillingPrice({ plan: futurePlan, tier: shop.tier })
          : 0;
      }

      if (!canceled) {
        state = state.set('total', state = state.get('total', 0) + shop.price);
      }

      break;
    }

    default: {
      break;
    }
  }

  return reduction.setIn(['billingV3', 'shops'], state.setIn(['byId', shopId], Map(shop)));
}

function* getShopRejected(reduction, { error, shop, signal }) {
  if (signal.aborted) return reduction;

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.setIn(['billingV3', 'shops', 'byId', shop.shopId], Map(shop));
}

function* manageShop(reduction, { type, ...rest } = {}) {
  abort(manageController);

  switch (type) {
    case MODALS.BILLING.MANAGE: {
      const { shopId, quote } = rest;

      if (quote) {
        const { channel } = rest;
        return reduction.setIn(['billingV3', 'manage'], Map({ channel, shop: Map(rest), shopId, type }));
      }

      const shop = reduction.getIn(['shops', 'byId', shopId]);

      if (!shop) return reduction;

      const channel = shop.get('channel');
      const db = shop.get('db');

      if (shop.get('syncStatus') === STATUSES.TOKEN_REJECTED) {
        if (isSubscribed(reduction.getIn(['user', 'subscriptions', shopId]))) {
          rest.reconnect = ENDPOINT.CONNECT_CHANNEL[channel];
        } else {
          navigateTo(`/${shopId}`);
          return reduction;
        }
      }

      yield function effect(dispatch) {
        const { signal } = manageController.start();
        api.billingV3
          .getSubscription({ db, shopId, signal })
          .then(
            (response) => dispatch(actions.billingV3.manageShop.fulfilled({ response, shop: rest })),
            (error) => dispatch(actions.billingV3.manageShop.rejected({ error, signal }))
          );
      };

      return reduction.setIn(['billingV3', 'manage'], Map({ channel, shopId, type, ...rest }));
    }

    case MODALS.BILLING.SUCCESS: {
      const { action, shopId } = rest;
      const shop = reduction.getIn(['shops', 'byId', shopId]);

      if (!shop) return reduction;

      const channel = shop.get('channel');
      const shopName = shop.get('name');

      return reduction.setIn(['billingV3', 'manage'], Map({ action, channel, shopId, shopName, type }));
    }

    default: {
      return reduction.deleteIn(['billingV3', 'manage']);
    }
  }
}

function* manageShopFulfilled(reduction, { response, shop: source }) {
  abort(manageController);

  const { shopId } = source;
  const { lite: { product_count: listings, sync_status: syncStatus }} = response;
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const subscription = reduction.getIn(['user', 'subscriptions', shopId]);

  if (syncStatus === STATUSES.TOKEN_REJECTED && shop.get('syncStatus') !== syncStatus) {
    yield function effect(dispatch) {
      dispatch(actions.shops.getShops());
      navigateTo(`/${shopId}`);
    };

    return reduction.setIn(['shops', 'byId', shopId, 'syncStatus'], syncStatus);
  }

  function updateData(data) {
    const result = {
      channel: shop.get('channel'),
      db: shop.get('db'),
      listings,
      quote: shapeQuoteForApp(response),
      shopId,
      shopName: shop.get('name'),
    };

    const plan = subscription.get('plan').split(SEPARATOR.DOT)[0];

    switch (plan) {
      case PLAN.LITE:
      case PLAN.PLUS: {
        result.plan = plan;
        result.futurePlan = PLAN.PLUS;
        result.tier = getTier({ listings });
        result.price = getBillingPrice(result);
        result.discount = subscription.get('discountPercent') || 0;
        result.dueDate = subscription.get('dueDate');
        result.canceled = !!subscription.get('cancelAtPeriodEnd');
        result.tierChange = !!subscription.get('tierChangeDetected');
        result.subscriptionId = subscription.get('subscriptionId');
        result.undiscountedPrice = result.discount > 0
          ? getBillingPrice({ plan: result.plan, tier: result.tier })
          : 0;

        break;
      }

      case PLAN.TRIAL: {
        result.plan = plan;
        result.futurePlan = PLAN.PLUS;
        result.trialExpired = subscription.get('trialRemaining') < 1;
        result.tier = getTier({ listings });
        result.futurePlan = PLAN.PLUS;
        result.discount = result.quote.getIn([result.futurePlan, 'discount']);
        result.price = getBillingPrice({ discount: result.discount, plan: result.futurePlan, tier: result.tier });
        result.undiscountedPrice = result.discount > 0
          ? getBillingPrice({ plan: result.futurePlan, tier: result.tier })
          : 0;

        break;
      }

      case LEGACY_TIER.T_0:
      case LEGACY_TIER.T_251:
      case LEGACY_TIER.T_501:
      case LEGACY_TIER.T_1001:
      case LEGACY_TIER.T_2501:
      case LEGACY_TIER.T_5001:
      case LEGACY_TIER.T_7501:
      case LEGACY_TIER.T_10001:
      case LEGACY_TIER.T_20001:
      case LEGACY_TIER.T_30001:
      case LEGACY_TIER.T_40001:
      case LEGACY_TIER.T_50001:
      case LEGACY_TIER.T_60001:
      case LEGACY_TIER.T_70001: {
        result.plan = PLAN.VIP;
        result.dueDate = subscription.get('dueDate');
        result.canceled = !!subscription.get('cancelAtPeriodEnd');
        result.tierChange = !!subscription.get('tierChangeDetected');
        result.subscriptionId = subscription.get('subscriptionId');
        result.futurePlan = PLAN.PLUS;

        if (isAfter(new Date(result.dueDate), BILLING_V3_TIMESTAMP)) {
          const futurePlan = isTrue(reduction.getIn(['user', `${BILLING_V3_PLUS_PREFERRED}${shopId}`]))
            ? PLAN.PLUS
            : PLAN.LITE;

          result.tier = getTier({ listings });
          result.discount = result.quote.getIn([futurePlan, 'discount']);
          result.price = getBillingPrice({ discount: result.discount, plan: futurePlan, tier: result.tier });
          result.undiscountedPrice = result.discount > 0
            ? getBillingPrice({ plan: futurePlan, tier: result.tier })
            : 0;
        } else {
          result.discount = subscription.get('discountPercent') || 0;
          result.price = subscription.get('price') || 0;
          result.undiscountedPrice = 0;

          if (result.discount > 0) {
            result.undiscountedPrice = result.price;
            result.price = getDiscountedPrice({ discount: result.discount, price: result.price });
          }
        }

        break;
      }

      default: {
        break;
      }
    }

    return data.set('shop', Map(result));
  }

  return reduction.updateIn(['billingV3', 'manage'], updateData);
}

function* manageShopRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(manageController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['billingV3', 'manage']);
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['billingV3', ...path]
    : ['billingV3', path];

  if (value) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* setInvoicesTab(reduction, index) {
  abort(invoicesController);

  const tab = reduction.getIn(['billingV3', 'invoices', 'tabs', index]);

  switch (tab.get('channel')) {
    case EBAY:
    case ETSY: {
      const shopId = tab.get('shopId');

      if (reduction.hasIn(['billingV3', 'invoices', 'byShopId', shopId])) break;

      const db = tab.get('db');
      const subscriptionId = tab.get('subscriptionId');

      yield function effect(dispatch) {
        const { signal } = invoicesController.start();
        api.billingV3
          .getInvoices({ db, shopId, signal, subscriptionId })
          .then(
            (response) => dispatch(actions.billingV3.setInvoicesTab.fulfilled({ index, response, shopId })),
            (error) => dispatch(actions.billingV3.setInvoicesTab.rejected({ error, signal }))
          );
      };

      break;
    }

    default: {
      break;
    }
  }

  return reduction.setIn(['billingV3', 'invoices', 'tab'], index);
}

function* setInvoicesTabFulfilled(reduction, { index, response, shopId }) {
  abort(invoicesController);

  if (getSize(response.invoices)) {
    return reduction.setIn(['billingV3', 'invoices', 'byShopId', shopId], fromJS(response.invoices));
  }

  const tabs = reduction.getIn(['billingV3', 'invoices', 'tabs']).delete(index);
  const tab = Math.min(index, getSize(tabs) - 1);

  if (getSize(tabs)) {
    yield function effect(dispatch) {
      dispatch(actions.billingV3.setInvoicesTab(tab));
    };
  }

  return reduction
    .setIn(['billingV3', 'invoices', 'tab'], tab)
    .setIn(['billingV3', 'invoices', 'tabs'], tabs);
}

function* setInvoicesTabRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(invoicesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_INVOICES,
      })
    );
  };

  return reduction;
}

function* togglePaymentMethodModal(reduction, paymentError ) {
  let billing = reduction.get('billingV3') || EMPTY.MAP;
  if (paymentError) {
    billing = billing.setIn(['paymentError'], paymentError);
  }

  billing = billing.getIn(['showPaymentMethodModal'])
    ? billing.deleteIn(['showPaymentMethodModal'])
    : billing.setIn(['showPaymentMethodModal'], true);

  return reduction.set('billingV3', billing);
}

const billingV3 = {
  [actions.billingV3.getCustomer]: getCustomer,
  [actions.billingV3.getCustomer.fulfilled]: getCustomerFulfilled,
  [actions.billingV3.getCustomer.rejected]: getCustomerRejected,
  [actions.billingV3.cleanUp]: cleanUp,
  [actions.billingV3.getInvoices]: getInvoices,
  [actions.billingV3.getShops]: getShops,
  [actions.billingV3.getShop]: getShop,
  [actions.billingV3.getShop.fulfilled]: getShopFulfilled,
  [actions.billingV3.getShop.rejected]: getShopRejected,
  [actions.billingV3.setInvoicesTab]: setInvoicesTab,
  [actions.billingV3.setInvoicesTab.fulfilled]: setInvoicesTabFulfilled,
  [actions.billingV3.setInvoicesTab.rejected]: setInvoicesTabRejected,
  [actions.billingV3.manageShop]: manageShop,
  [actions.billingV3.manageShop.fulfilled]: manageShopFulfilled,
  [actions.billingV3.manageShop.rejected]: manageShopRejected,
  [actions.billingV3.setData]: setData,
  [actions.billingV3.togglePaymentMethodModal]: togglePaymentMethodModal,
};

export default billingV3;
